import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  submitApplication,
  selectApplication,
  selectAppSubmitting,
  selectAppError,
  clearApplicationError
} from '../../../redux/slices/applicationSlice';
import { StepCard, StepActions, Button } from '../../../components/stepCard';
import { formatRupees } from '../../../utils/loanMath';
import PageTransition from '../../../components/pageTransition';
import { notify } from '../../../utils/notify';
import { clearExtraction } from '../../../redux/slices/applicationSlice';
import './index.scss';

const LABELS = {
  gender: { MALE: 'Male', FEMALE: 'Female', OTHER: 'Other' },
  residenceType: { RENTED: 'Rented', OWNED: 'Owned', COMPANY_PROVIDED: 'Company provided' },
  employmentType: {
    SALARIED: 'Salaried',
    SELF_EMPLOYED: 'Self-employed',
    BUSINESS_OWNER: 'Business owner'
  },
  loanPurpose: {
    HOME_RENOVATION: 'Home renovation',
    MEDICAL_EMERGENCY: 'Medical emergency',
    EDUCATION: 'Education',
    VEHICLE_PURCHASE: 'Vehicle purchase',
    BUSINESS_EXPANSION: 'Business expansion',
    DEBT_CONSOLIDATION: 'Debt consolidation'
  },
  docType: {
    AADHAAR_CARD: 'Aadhaar card',
    PAN_CARD: 'PAN card',
    SALARY_SLIP: 'Salary slips',
    BANK_STATEMENT: 'Bank statement',
    ITR: 'Income tax return',
    FORM_16: 'Form 16'
  }
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const ageFrom = (iso) => {
  if (!iso) return null;
  const dob = new Date(iso);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age;
};

// One review block. `editPath` sends the applicant back to the step that owns it.
const ReviewGroup = ({ title, editPath, onEdit, rows }) => (
  <section className="review-group">
    <header className="review-group__head">
      <h2 className="review-group__title">{title}</h2>
      <button type="button" className="review-group__edit" onClick={() => onEdit(editPath)}>
        Edit
      </button>
    </header>

    <dl className="review-group__list">
      {rows.map(({ label, value, mono }) => (
        <div key={label} className="review-row">
          <dt className="review-row__label">{label}</dt>
          <dd className={`review-row__value ${mono ? 'numeric' : ''}`}>{value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  </section>
);

const ReviewStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const application = useSelector(selectApplication);
  const submitting = useSelector(selectAppSubmitting);
  const serverError = useSelector(selectAppError);

  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(null);

  const goEdit = (path) => navigate(path);

  const handleSubmit = async () => {
    if (!consent) {
      setConsentError('Please confirm the declaration before submitting');
      return;
    }

    const result = await dispatch(submitApplication({ id: application.id }));

    if (submitApplication.fulfilled.match(result)) {
      notify.success('Application submitted successfully');
      dispatch(clearExtraction());
      navigate('/apply/status');
    } else {
      notify.error(result.payload);
    }
  };

  const calc = application.calculated ?? {};
  const age = ageFrom(application.dateOfBirth);

  const documentRows = (application.documents ?? []).reduce((acc, doc) => {
    const label = LABELS.docType[doc.docType] ?? doc.docType;
    acc[label] = (acc[label] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <PageTransition>
      <StepCard
        title="Review your application"
        description="Check everything below before you submit. You won’t be able to edit after this.">
        <div className="review-groups">
          <ReviewGroup
            title="Personal details"
            editPath="/apply/personal"
            onEdit={goEdit}
            rows={[
              { label: 'Name', value: `${application.firstName} ${application.lastName}` },
              {
                label: 'Date of birth',
                value: `${formatDate(application.dateOfBirth)}${age ? ` · age ${age}` : ''}`
              },
              { label: 'Gender', value: LABELS.gender[application.gender] },
              { label: 'Mobile', value: application.mobileNumber, mono: true },
              { label: 'Email', value: application.contactEmail },
              {
                label: 'Address',
                value: [
                  application.currentAddress,
                  application.city,
                  application.state,
                  application.pinCode
                ]
                  .filter(Boolean)
                  .join(', ')
              },
              { label: 'Residence', value: LABELS.residenceType[application.residenceType] }
            ]}
          />

          <ReviewGroup
            title="Identity and employment"
            editPath="/apply/kyc"
            onEdit={goEdit}
            rows={[
              {
                label: 'Aadhaar',
                value: application.aadhaarLast4 ? `XXXX XXXX ${application.aadhaarLast4}` : null,
                mono: true
              },
              { label: 'PAN', value: application.panNumber, mono: true },
              { label: 'Employment', value: LABELS.employmentType[application.employmentType] },
              { label: 'Employer', value: application.employerName },
              {
                label: 'Years there',
                value:
                  application.yearsAtEmployer != null ? `${application.yearsAtEmployer}` : null,
                mono: true
              },
              {
                label: 'Monthly income',
                value: formatRupees(application.monthlyIncome),
                mono: true
              }
            ]}
          />

          <ReviewGroup
            title="Loan request"
            editPath="/apply/loan"
            onEdit={goEdit}
            rows={[
              { label: 'Amount', value: formatRupees(application.loanAmount), mono: true },
              { label: 'Tenure', value: `${application.tenureMonths} months`, mono: true },
              { label: 'Purpose', value: LABELS.loanPurpose[application.loanPurpose] },
              {
                label: 'EMI date',
                value: application.preferredEmiDay
                  ? `${application.preferredEmiDay} of each month`
                  : null
              },
              {
                label: 'Estimated EMI',
                value: calc.emi ? `${formatRupees(calc.emi)} at ${calc.annualRatePct}% p.a.` : null,
                mono: true
              },
              {
                label: 'Share of income',
                value: calc.foirPct != null ? `${calc.foirPct}%` : null,
                mono: true
              }
            ]}
          />

          <ReviewGroup
            title="Documents"
            editPath="/apply/documents"
            onEdit={goEdit}
            rows={Object.entries(documentRows).map(([label, count]) => ({
              label,
              value: count > 1 ? `${count} files uploaded` : 'Uploaded'
            }))}
          />
        </div>

        <div className="disclaimer">
          <span className="disclaimer__title">Before you submit</span>
          <p className="disclaimer__body">
            Any system-generated recommendation is based only on the information you have provided.
            Every application is reviewed by a qualified underwriter, who makes the final decision.
            This process follows RBI guidelines and the DPDP Act.
          </p>
        </div>

        <label className={`consent ${consentError ? 'consent--error' : ''}`}>
          <input
            type="checkbox"
            className="consent__box"
            checked={consent}
            onChange={(e) => {
              setConsent(e.target.checked);
              setConsentError(null);
              if (serverError) dispatch(clearApplicationError());
            }}
          />
          <span className="consent__text">
            I confirm the information above is accurate, and I consent to verification of my
            documents and details with government and third-party sources.
          </span>
        </label>

        {consentError && (
          <p className="form-error" role="alert">
            {consentError}
          </p>
        )}
        {serverError && (
          <p className="form-error" role="alert">
            {serverError}
          </p>
        )}

        <StepActions onBack={() => navigate('/apply/documents')}>
          <Button type="button" onClick={handleSubmit} loading={submitting}>
            {submitting ? 'Submitting…' : 'Submit application'}
          </Button>
        </StepActions>
      </StepCard>
    </PageTransition>
  );
};
ReviewGroup.propTypes = {
  title: PropTypes.string.isRequired,
  editPath: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.node,
      mono: PropTypes.bool
    })
  ).isRequired
};
export default ReviewStep;
